/**
 * LinguistPro Auth Store
 * Manages Supabase authentication + user profile state.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../types';

// ── State + Actions ────────────────────────────────────────────

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
}

interface AuthActions {
  setSession: (session: Session | null) => void;
  loadProfile: () => Promise<void>;
  /** Alias for loadProfile — use after org membership changes (C5) */
  refreshProfile: () => Promise<void>;
  /** Resolves true when the profile was re-read, false when the read failed (cached values kept). */
  refreshBalance: () => Promise<boolean>;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  setOnboarded: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

// ── Store ──────────────────────────────────────────────────────

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      user: null,
      session: null,
      profile: null,
      loading: true,
      error: null,

      setSession: (session) => {
        set(
          { session, user: session?.user ?? null, error: null },
          false,
          'setSession'
        );
        if (session?.user) {
          get().loadProfile();
        } else {
          set({ profile: null, loading: false }, false, 'clearProfile');
        }
      },

      loadProfile: async () => {
        const { user } = get();
        if (!user) return;

        const { data, error } = await supabase
          .from('lp_user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Failed to load profile:', error.message);
          set({ loading: false }, false, 'loadProfile/error');
          return;
        }

        set({ profile: data as UserProfile, loading: false }, false, 'loadProfile');
      },

      refreshProfile: async () => {
        return get().loadProfile();
      },

      refreshBalance: async () => {
        const { user } = get();
        if (!user) return false;

        // '*' so the legacy column is read when it exists and its absence (before migration 031) is not an error.
        const { data, error } = await supabase
          .from('lp_user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        if (error) console.warn('[refreshBalance] profile refresh failed:', error.message);

        if (!error && data) {
          set(
            (state) => ({
              profile: state.profile
                ? {
                    ...state.profile,
                    balance_seconds: data.balance_seconds,
                    plan: data.plan,
                    legacy_paid_seconds: data.legacy_paid_seconds,
                  }
                : null,
            }),
            false,
            'refreshBalance'
          );
        }
        return !error && !!data;
      },

      signUp: async (email, password) => {
        set({ loading: true, error: null }, false, 'signUp/start');
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) {
          set({ loading: false, error: error.message }, false, 'signUp/error');
          throw error;
        }
        set({ loading: false }, false, 'signUp/done');
      },

      signIn: async (email, password) => {
        set({ loading: true, error: null }, false, 'signIn/start');
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          set({ loading: false, error: error.message }, false, 'signIn/error');
          throw error;
        }
        set({ loading: false }, false, 'signIn/done');
      },

      signInWithGoogle: async () => {
        set({ loading: true, error: null }, false, 'signInWithGoogle/start');
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: `${window.location.origin}/app` },
        });
        if (error) {
          set({ loading: false, error: error.message }, false, 'signInWithGoogle/error');
          throw error;
        }
      },

      signOut: async () => {
        await supabase.auth.signOut();
        set(
          { user: null, session: null, profile: null, loading: false, error: null },
          false,
          'signOut'
        );
      },

      resetPassword: async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth?mode=reset`,
        });
        if (error) throw error;
      },

      setOnboarded: async () => {
        const { user, profile } = get();
        if (!user || !profile) return;

        const { error } = await supabase
          .from('lp_user_profiles')
          .update({ onboarded: true })
          .eq('id', user.id);

        if (error) {
          console.error('setOnboarded DB error:', error);
          return; // Don't update local state if the DB write failed
        }

        set(
          { profile: { ...profile, onboarded: true } },
          false,
          'setOnboarded'
        );
      },
    }),
    { name: 'LinguistPro-Auth' }
  )
);

// ── Selector hooks ─────────────────────────────────────────────

export const useUser = () => useAuthStore((s) => s.user);
export const useSession = () => useAuthStore((s) => s.session);
export const useProfile = () => useAuthStore((s) => s.profile);
export const useAuthLoading = () => useAuthStore((s) => s.loading);
export const useAuthError = () => useAuthStore((s) => s.error);
